import { useEffect, useState, type ChangeEvent } from 'react';
import { useCms } from './CmsContext';
import type { FormField, FormStep } from '../../content/applicationForm';

const KINDS: Array<{ value: FormField['kind']; label: string }> = [
  { value: 'text', label: 'Short text' },
  { value: 'email', label: 'Email' },
  { value: 'tel', label: 'Phone' },
  { value: 'textarea', label: 'Long text' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Choice cards' },
];

// ApplicationPage and the existing API derive the applicant identity from these
// answer keys. They can be reordered or moved, but keeping them prevents an
// editor from accidentally publishing a form that the submission API rejects.
const PROTECTED_FIELD_IDS = new Set(['firstName', 'lastName', 'email']);

type DeleteTarget = { kind: 'step' | 'field'; id: string } | null;

function createId(prefix: string) {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function moveItem<T>(items: T[], index: number, offset: -1 | 1) {
  const destination = index + offset;
  if (index < 0 || destination < 0 || destination >= items.length) return items;
  const next = [...items];
  [next[index], next[destination]] = [next[destination], next[index]];
  return next;
}

function fieldKindLabel(kind: FormField['kind']) {
  return KINDS.find((item) => item.value === kind)?.label ?? kind;
}

export function FormBuilder() {
  const { steps, setSteps, resetSteps, litters } = useCms();
  const [selectedStepId, setSelectedStepId] = useState(steps[0]?.id ?? '');
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [previewStepId, setPreviewStepId] = useState(steps[0]?.id ?? '');
  const [previewValues, setPreviewValues] = useState<Record<string, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const selectedStep = steps.find((step) => step.id === selectedStepId) ?? steps[0];
  const selectedField = selectedStep?.fields.find((field) => field.id === selectedFieldId) ?? null;
  const previewStep = steps.find((step) => step.id === previewStepId) ?? selectedStep ?? steps[0];
  const previewIndex = previewStep ? steps.findIndex((step) => step.id === previewStep.id) : -1;

  useEffect(() => {
    if (selectedStep && selectedStep.id !== selectedStepId) {
      setSelectedStepId(selectedStep.id);
      setSelectedFieldId(null);
    }
    if (selectedStep && !steps.some((step) => step.id === previewStepId)) setPreviewStepId(selectedStep.id);
  }, [previewStepId, selectedStep, selectedStepId, steps]);

  useEffect(() => {
    if (selectedFieldId && !selectedField) setSelectedFieldId(null);
  }, [selectedField, selectedFieldId]);

  const selectStep = (id: string) => {
    setSelectedStepId(id);
    setSelectedFieldId(null);
    setPreviewStepId(id);
    setDeleteTarget(null);
  };

  const selectField = (id: string) => {
    setSelectedFieldId(id);
    if (selectedStep) setPreviewStepId(selectedStep.id);
    setDeleteTarget(null);
  };

  const updateStep = (id: string, patch: Partial<FormStep>) => {
    setSteps((items) => items.map((step) => (step.id === id ? { ...step, ...patch } : step)));
  };

  const updateField = (stepId: string, id: string, patch: Partial<FormField>) => {
    setSteps((items) => items.map((step) => (
      step.id === stepId
        ? { ...step, fields: step.fields.map((field) => (field.id === id ? { ...field, ...patch } : field)) }
        : step
    )));
  };

  const addStep = () => {
    const step: FormStep = { id: createId('step'), title: 'New step', fields: [] };
    setSteps((items) => [...items, step]);
    selectStep(step.id);
  };

  const duplicateStep = (step: FormStep) => {
    const copy: FormStep = {
      ...step,
      id: createId('step'),
      title: `${step.title || 'Untitled step'} copy`,
      fields: step.fields.map((field) => ({ ...field, id: createId('field') })),
    };
    setSteps((items) => {
      const index = items.findIndex((item) => item.id === step.id);
      const next = [...items];
      next.splice(index + 1, 0, copy);
      return next;
    });
    selectStep(copy.id);
  };

  const reorderStep = (id: string, offset: -1 | 1) => {
    setSteps((items) => moveItem(items, items.findIndex((step) => step.id === id), offset));
  };

  const removeStep = (step: FormStep) => {
    const index = steps.findIndex((item) => item.id === step.id);
    const nextSelected = steps[index + 1] ?? steps[index - 1];
    setSteps((items) => items.filter((item) => item.id !== step.id));
    setSelectedStepId(nextSelected?.id ?? '');
    setPreviewStepId(nextSelected?.id ?? '');
    setSelectedFieldId(null);
    setDeleteTarget(null);
  };

  const addField = () => {
    if (!selectedStep) return;
    const field: FormField = { id: createId('field'), label: 'New question', kind: 'text' };
    updateStep(selectedStep.id, { fields: [...selectedStep.fields, field] });
    setSelectedFieldId(field.id);
    setPreviewStepId(selectedStep.id);
  };

  const duplicateField = (field: FormField) => {
    if (!selectedStep) return;
    const copy: FormField = { ...field, id: createId('field'), label: `${field.label || 'Untitled question'} copy` };
    const index = selectedStep.fields.findIndex((item) => item.id === field.id);
    const fields = [...selectedStep.fields];
    fields.splice(index + 1, 0, copy);
    updateStep(selectedStep.id, { fields });
    setSelectedFieldId(copy.id);
  };

  const reorderField = (id: string, offset: -1 | 1) => {
    if (!selectedStep) return;
    updateStep(selectedStep.id, { fields: moveItem(selectedStep.fields, selectedStep.fields.findIndex((field) => field.id === id), offset) });
  };

  const moveFieldToStep = (field: FormField, targetStepId: string) => {
    if (!selectedStep || targetStepId === selectedStep.id) return;
    setSteps((items) => items.map((step) => {
      if (step.id === selectedStep.id) return { ...step, fields: step.fields.filter((item) => item.id !== field.id) };
      if (step.id === targetStepId) return { ...step, fields: [...step.fields, field] };
      return step;
    }));
    setSelectedStepId(targetStepId);
    setSelectedFieldId(field.id);
    setPreviewStepId(targetStepId);
    setDeleteTarget(null);
  };

  const removeField = (field: FormField) => {
    if (!selectedStep || PROTECTED_FIELD_IDS.has(field.id)) return;
    updateStep(selectedStep.id, { fields: selectedStep.fields.filter((item) => item.id !== field.id) });
    setSelectedFieldId(null);
    setDeleteTarget(null);
  };

  const restoreDefaults = () => {
    resetSteps();
    setSelectedStepId('about');
    setPreviewStepId('about');
    setSelectedFieldId(null);
    setDeleteTarget(null);
    setConfirmReset(false);
  };

  const syncLitterChoices = () => {
    const choices = litters
      .filter((litter) => litter.status !== 'reserved')
      .map((litter) => {
        if (!litter.expectedDate) return litter.title;
        const date = new Date(`${litter.expectedDate}T12:00:00`);
        const timing = Number.isNaN(date.getTime()) ? litter.expectedDate : date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        return `${litter.title} · ${timing}`;
      });
    choices.push('No preference, whichever is the best fit');
    setSteps((items) => items.map((step) => ({
      ...step,
      fields: step.fields.map((field) => field.id === 'litterPreference' ? { ...field, kind: 'radio', options: choices } : field),
    })));
  };

  if (!selectedStep) {
    return (
      <div className="cms-page cms-page-full">
        <style>{FORM_BUILDER_STYLES}</style>
        <div className="cms-page-head"><h1>Application form</h1><p>Build the questions families complete before joining your puppy waitlist.</p></div>
        <section className="cms-card form-builder-empty">
          <h2>Your form has no steps</h2>
          <p>Add a step to start rebuilding the applicant experience.</p>
          <button type="button" className="cms-btn cms-btn-primary" onClick={addStep}>Add first step</button>
        </section>
      </div>
    );
  }

  const selectedStepIndex = steps.findIndex((step) => step.id === selectedStep.id);
  const protectedFields = selectedStep.fields.filter((field) => PROTECTED_FIELD_IDS.has(field.id));
  const canDeleteStep = steps.length > 1 && protectedFields.length === 0;

  return (
    <div className="cms-page cms-page-full form-builder-page">
      <style>{FORM_BUILDER_STYLES}</style>
      <div className="cms-page-head cms-page-head-row">
        <div>
          <h1>Application form</h1>
          <p>Build the steps and questions families complete at <a className="cms-link" href="/apply" target="_blank" rel="noreferrer">/apply ↗</a></p>
        </div>
        <div className="form-builder-reset">
          {confirmReset ? (
            <>
              <span>Replace your draft with the default form?</span>
              <button type="button" className="cms-btn cms-btn-danger" onClick={restoreDefaults}>Confirm reset</button>
              <button type="button" className="cms-btn cms-btn-ghost" onClick={() => setConfirmReset(false)}>Cancel</button>
            </>
          ) : <button type="button" className="cms-btn cms-btn-ghost" onClick={() => setConfirmReset(true)}>Reset to defaults</button>}
        </div>
      </div>

      <div className="form-builder-summary" role="status">
        <strong>{steps.length} step{steps.length === 1 ? '' : 's'}</strong>
        <span>·</span>
        <span>{steps.reduce((count, step) => count + step.fields.length, 0)} questions</span>
        <span>Changes appear in the preview now and go live when you save.</span>
      </div>

      <div className="form-builder-workspace">
        <aside className="form-builder-panel form-builder-steps" aria-label="Application steps">
          <header className="form-builder-panel-head">
            <div><span className="cms-eyebrow">Form structure</span><h2>Steps</h2></div>
          </header>
          <div className="form-builder-list">
            {steps.map((step, index) => (
              <article key={step.id} className={`form-builder-list-item ${selectedStep.id === step.id ? 'is-selected' : ''}`}>
                <button type="button" className="form-builder-select" aria-label={`Step ${index + 1}: ${step.title || 'Untitled step'}, ${step.fields.length} question${step.fields.length === 1 ? '' : 's'}`} aria-current={selectedStep.id === step.id ? 'step' : undefined} onClick={() => selectStep(step.id)}>
                  <span className="form-builder-number">{index + 1}</span>
                  <span><strong>{step.title || 'Untitled step'}</strong><small>{step.fields.length} question{step.fields.length === 1 ? '' : 's'}</small></span>
                </button>
                <div className="form-builder-item-actions">
                  <button type="button" aria-label={`Move ${step.title} step up`} title="Move up" disabled={index === 0} onClick={() => reorderStep(step.id, -1)}>↑</button>
                  <button type="button" aria-label={`Move ${step.title} step down`} title="Move down" disabled={index === steps.length - 1} onClick={() => reorderStep(step.id, 1)}>↓</button>
                  <button type="button" aria-label={`Duplicate ${step.title} step`} title="Duplicate" onClick={() => duplicateStep(step)}>⧉</button>
                </div>
              </article>
            ))}
          </div>
          <button type="button" className="cms-add cms-add-sm" onClick={addStep}>+ Add step</button>
        </aside>

        <section className="form-builder-panel form-builder-questions" aria-label={`Questions in ${selectedStep.title}`}>
          <header className="form-builder-panel-head">
            <div><span className="cms-eyebrow">Step {selectedStepIndex + 1}</span><h2>{selectedStep.title || 'Untitled step'}</h2></div>
            <button type="button" className={`form-builder-text-button ${selectedField ? '' : 'is-active'}`} onClick={() => { setSelectedFieldId(null); setDeleteTarget(null); }}>Step settings</button>
          </header>
          <div className="form-builder-list">
            {selectedStep.fields.length ? selectedStep.fields.map((field, index) => (
              <article key={field.id} className={`form-builder-question ${selectedField?.id === field.id ? 'is-selected' : ''}`}>
                <button type="button" className="form-builder-select" aria-label={`Question: ${field.label || 'Untitled question'}, ${fieldKindLabel(field.kind)}${field.required ? ', required' : ''}`} aria-current={selectedField?.id === field.id ? 'true' : undefined} onClick={() => selectField(field.id)}>
                  <span className="form-builder-drag" aria-hidden>⋮⋮</span>
                  <span><strong>{field.label || 'Untitled question'}</strong><small>{fieldKindLabel(field.kind)}{field.required ? ' · Required' : ''}</small></span>
                </button>
                <div className="form-builder-item-actions">
                  <button type="button" aria-label={`Move ${field.label} question up`} title="Move up" disabled={index === 0} onClick={() => reorderField(field.id, -1)}>↑</button>
                  <button type="button" aria-label={`Move ${field.label} question down`} title="Move down" disabled={index === selectedStep.fields.length - 1} onClick={() => reorderField(field.id, 1)}>↓</button>
                  <button type="button" aria-label={`Duplicate ${field.label} question`} title="Duplicate" onClick={() => duplicateField(field)}>⧉</button>
                </div>
              </article>
            )) : <div className="form-builder-list-empty"><strong>No questions in this step</strong><span>Add one below, or move a question here from another step.</span></div>}
          </div>
          <button type="button" className="cms-add cms-add-sm" onClick={addField}>+ Add question</button>
        </section>

        <aside className="form-builder-side">
          <section className="form-builder-panel form-builder-inspector">
            <header className="form-builder-panel-head">
              <div><span className="cms-eyebrow">Settings</span><h2>{selectedField ? 'Question' : 'Step'}</h2></div>
              {selectedField && <button type="button" className="form-builder-text-button" onClick={() => setSelectedFieldId(null)}>Close</button>}
            </header>

            {selectedField ? (
              <div className="form-builder-settings">
                <label className="cms-field"><span>Question label</span><input value={selectedField.label} onChange={(event) => updateField(selectedStep.id, selectedField.id, { label: event.target.value })} /></label>
                <label className="cms-field"><span>Answer type</span>
                  <select value={selectedField.kind} disabled={PROTECTED_FIELD_IDS.has(selectedField.id)} onChange={(event) => updateField(selectedStep.id, selectedField.id, { kind: event.target.value as FormField['kind'] })}>
                    {KINDS.map((kind) => <option key={kind.value} value={kind.value}>{kind.label}</option>)}
                  </select>
                </label>
                {selectedField.kind === 'select' || selectedField.kind === 'radio' ? (
                  <label className="cms-field"><span>Options</span><textarea rows={5} aria-describedby="field-options-hint" value={selectedField.options?.join('\n') ?? ''} onChange={(event) => updateField(selectedStep.id, selectedField.id, { options: event.target.value.split('\n').map((item) => item.trim()).filter(Boolean) })} /><small id="field-options-hint">One option per line.</small></label>
                ) : (
                  <label className="cms-field"><span>Placeholder</span><input value={selectedField.placeholder ?? ''} onChange={(event) => updateField(selectedStep.id, selectedField.id, { placeholder: event.target.value })} /></label>
                )}
                {selectedField.id === 'litterPreference' && (
                  <div className="form-builder-sync">
                    <div><strong>Connected to Litters</strong><span>Replace these choices with every planned or available litter.</span></div>
                    <button type="button" className="cms-btn cms-btn-ghost" onClick={syncLitterChoices}>Sync choices</button>
                  </div>
                )}
                <label className="cms-checkbox"><input type="checkbox" checked={PROTECTED_FIELD_IDS.has(selectedField.id) || (selectedField.required ?? false)} disabled={PROTECTED_FIELD_IDS.has(selectedField.id)} onChange={(event) => updateField(selectedStep.id, selectedField.id, { required: event.target.checked })} /> Required question</label>
                {steps.length > 1 && (
                  <label className="cms-field"><span>Move to another step</span>
                    <select value="" onChange={(event) => event.target.value && moveFieldToStep(selectedField, event.target.value)}>
                      <option value="">Choose a step…</option>
                      {steps.filter((step) => step.id !== selectedStep.id).map((step) => <option key={step.id} value={step.id}>{step.title}</option>)}
                    </select>
                  </label>
                )}
                <div className="form-builder-inspector-actions">
                  <button type="button" className="cms-btn cms-btn-ghost" onClick={() => duplicateField(selectedField)}>Duplicate question</button>
                  {PROTECTED_FIELD_IDS.has(selectedField.id) ? (
                    <p className="form-builder-protected">This contact field is required by application delivery and cannot be deleted.</p>
                  ) : deleteTarget?.kind === 'field' && deleteTarget.id === selectedField.id ? (
                    <div className="form-builder-confirm">
                      <p>Delete “{selectedField.label || 'Untitled question'}”? This takes effect when you save.</p>
                      <button type="button" className="cms-btn cms-btn-danger" onClick={() => removeField(selectedField)}>Confirm delete</button>
                      <button type="button" className="cms-btn cms-btn-ghost" onClick={() => setDeleteTarget(null)}>Cancel</button>
                    </div>
                  ) : <button type="button" className="cms-btn cms-btn-danger" onClick={() => setDeleteTarget({ kind: 'field', id: selectedField.id })}>Delete question</button>}
                </div>
              </div>
            ) : (
              <div className="form-builder-settings">
                <label className="cms-field"><span>Step title</span><input value={selectedStep.title} onChange={(event) => updateStep(selectedStep.id, { title: event.target.value })} /></label>
                <p className="form-builder-help">Applicants see this title above the questions in this step.</p>
                <div className="form-builder-inspector-actions">
                  <button type="button" className="cms-btn cms-btn-ghost" onClick={() => duplicateStep(selectedStep)}>Duplicate step</button>
                  {!canDeleteStep ? (
                    <p className="form-builder-protected">
                      {steps.length === 1 ? 'A published application needs at least one step.' : `Move ${protectedFields.map((field) => field.label).join(', ')} to another step before deleting this step.`}
                    </p>
                  ) : deleteTarget?.kind === 'step' && deleteTarget.id === selectedStep.id ? (
                    <div className="form-builder-confirm">
                      <p>Delete “{selectedStep.title || 'Untitled step'}” and all of its questions?</p>
                      <button type="button" className="cms-btn cms-btn-danger" onClick={() => removeStep(selectedStep)}>Confirm delete</button>
                      <button type="button" className="cms-btn cms-btn-ghost" onClick={() => setDeleteTarget(null)}>Cancel</button>
                    </div>
                  ) : <button type="button" className="cms-btn cms-btn-danger" onClick={() => setDeleteTarget({ kind: 'step', id: selectedStep.id })}>Delete step</button>}
                </div>
              </div>
            )}
          </section>

          <ApplicationPreview
            steps={steps}
            step={previewStep}
            stepIndex={previewIndex}
            selectedFieldId={selectedField?.id ?? null}
            values={previewValues}
            onValue={(id, value) => setPreviewValues((current) => ({ ...current, [id]: value }))}
            onNavigate={(index) => setPreviewStepId(steps[index].id)}
            onEdit={() => previewStep && selectStep(previewStep.id)}
          />
        </aside>
      </div>
    </div>
  );
}

function ApplicationPreview({ steps, step, stepIndex, selectedFieldId, values, onValue, onNavigate, onEdit }: {
  steps: FormStep[];
  step: FormStep;
  stepIndex: number;
  selectedFieldId: string | null;
  values: Record<string, string>;
  onValue: (id: string, value: string) => void;
  onNavigate: (index: number) => void;
  onEdit: () => void;
}) {
  return (
    <section className="form-builder-preview" aria-label="Live applicant preview">
      <header>
        <div><span className="cms-eyebrow">Live preview</span><h2>Applicant view</h2></div>
        <button type="button" className="form-builder-text-button" onClick={onEdit}>Edit this step</button>
      </header>
      <div className="form-builder-preview-window">
        <div className="form-builder-preview-progress"><span>STEP {stepIndex + 1} / {steps.length}</span><i style={{ width: `${((stepIndex + 1) / (steps.length + 1)) * 100}%` }} /></div>
        <h3>{step.title || 'Untitled step'}</h3>
        <div className="form-builder-preview-fields">
          {step.fields.length ? step.fields.map((field) => (
            <PreviewField key={field.id} field={field} value={values[field.id] ?? ''} selected={field.id === selectedFieldId} onChange={(value) => onValue(field.id, value)} />
          )) : <p>This step does not have any questions yet.</p>}
        </div>
        <footer>
          <button type="button" disabled={stepIndex <= 0} onClick={() => onNavigate(stepIndex - 1)}>← Back</button>
          <button type="button" disabled={stepIndex >= steps.length - 1} onClick={() => onNavigate(stepIndex + 1)}>{stepIndex === steps.length - 1 ? 'Review answers →' : 'Next →'}</button>
        </footer>
      </div>
    </section>
  );
}

function PreviewField({ field, value, selected, onChange }: { field: FormField; value: string; selected: boolean; onChange: (value: string) => void }) {
  const id = `form-builder-preview-${field.id}`;
  const common = { id, name: id, value, onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => onChange(event.target.value) };
  return (
    <div className={`form-builder-preview-field ${selected ? 'is-selected' : ''}`}>
      <label id={`${id}-label`} htmlFor={id}>{field.label || 'Untitled question'} {field.required && <span>*</span>}</label>
      {field.kind === 'textarea' ? <textarea {...common} rows={3} placeholder={field.placeholder} />
        : field.kind === 'select' ? <select {...common}><option value="">Select an option</option>{field.options?.map((option) => <option key={option} value={option}>{option}</option>)}</select>
          : field.kind === 'radio' ? <div className="form-builder-preview-options" role="radiogroup" aria-labelledby={`${id}-label`}>{field.options?.length ? field.options.map((option) => <button type="button" key={option} className={value === option ? 'is-selected' : ''} onClick={() => onChange(option)}><i />{option}</button>) : <small>Add options to preview this question.</small>}</div>
            : <input {...common} type={field.kind} inputMode={field.kind === 'tel' ? 'numeric' : undefined} placeholder={field.placeholder} />}
    </div>
  );
}

const FORM_BUILDER_STYLES = `
  .form-builder-page { --fb-line: var(--cms-line, #dedede); --fb-panel: var(--cms-panel, #fff); --fb-ink: var(--cms-ink, #303030); --fb-muted: var(--cms-muted, #616161); --fb-accent: var(--cms-focus, #005bd3); }
  .form-builder-reset { display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 8px; }
  .form-builder-reset > span { color: var(--fb-muted); font-size: 13px; font-weight: 700; }
  .form-builder-summary { display: flex; align-items: center; flex-wrap: wrap; gap: 7px; margin: -8px 0 18px; padding: 10px 14px; border: 1px solid var(--fb-line); border-radius: 10px; background: #fff; color: var(--fb-muted); font-size: 13px; }
  .form-builder-summary strong { color: var(--fb-ink); }
  .form-builder-summary span:last-child { margin-left: auto; }
  .form-builder-workspace { display: grid; grid-template-columns: minmax(190px, .7fr) minmax(260px, 1fr) minmax(330px, 1.25fr); gap: 14px; align-items: start; }
  .form-builder-panel, .form-builder-preview { min-width: 0; border: 1px solid var(--fb-line); border-radius: 12px; background: var(--fb-panel); overflow: hidden; box-shadow: var(--cms-shadow); }
  .form-builder-panel-head, .form-builder-preview > header { display: flex; align-items: center; justify-content: space-between; gap: 10px; min-height: 58px; padding: 14px 16px; border-bottom: 1px solid var(--fb-line); }
  .form-builder-panel-head .cms-eyebrow, .form-builder-preview .cms-eyebrow { display: block; margin: 0 0 2px; }
  .form-builder-panel-head h2, .form-builder-preview h2 { margin: 0; font-family: inherit; font-size: 15px; line-height: 1.2; }
  .form-builder-list { display: grid; gap: 7px; padding: 10px; }
  .form-builder-list-item, .form-builder-question { border: 1px solid var(--fb-line); border-radius: 10px; background: #fff; overflow: hidden; }
  .form-builder-list-item.is-selected, .form-builder-question.is-selected { border-color: var(--fb-accent); box-shadow: 0 0 0 1px var(--fb-accent) inset; background: #f3f7ff; }
  .form-builder-select { width: 100%; min-width: 0; display: flex; align-items: center; gap: 9px; padding: 10px 11px 8px; border: 0; background: transparent; color: var(--fb-ink); text-align: left; cursor: pointer; }
  .form-builder-select > span:last-child { min-width: 0; display: grid; gap: 2px; }
  .form-builder-select strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13.5px; }
  .form-builder-select small { color: var(--fb-muted); font-size: 11.5px; }
  .form-builder-number { flex: 0 0 25px; width: 25px; height: 25px; display: grid; place-items: center; border-radius: 7px; background: #ededed; color: var(--fb-ink); font-size: 12px; font-weight: 700; }
  .form-builder-drag { flex: 0 0 auto; color: #aaa; font-size: 13px; letter-spacing: -3px; }
  .form-builder-item-actions { display: grid; grid-template-columns: repeat(3, 1fr); border-top: 1px solid var(--fb-line); }
  .form-builder-item-actions button { min-height: 29px; border: 0; border-right: 1px solid var(--fb-line); background: #f7f7f7; color: var(--fb-muted); cursor: pointer; }
  .form-builder-item-actions button:last-child { border-right: 0; }
  .form-builder-item-actions button:hover:not(:disabled) { background: #eaeaea; color: var(--fb-ink); }
  .form-builder-item-actions button:disabled { opacity: .35; cursor: not-allowed; }
  .form-builder-panel > .cms-add { width: calc(100% - 20px); margin: 0 10px 10px; }
  .form-builder-list-empty { display: grid; gap: 4px; padding: 24px 14px; border: 1px dashed var(--fb-line); border-radius: 10px; color: var(--fb-muted); font-size: 12.5px; text-align: center; }
  .form-builder-list-empty strong { color: var(--fb-ink); font-size: 14px; }
  .form-builder-side { display: grid; gap: 14px; min-width: 0; }
  .form-builder-settings { display: grid; gap: 14px; padding: 16px; }
  .form-builder-settings .cms-field small { color: var(--fb-muted); font-size: 12px; }
  .form-builder-help, .form-builder-protected { margin: 0; color: var(--fb-muted); font-size: 12.5px; line-height: 1.45; }
  .form-builder-sync { display: grid; gap: 9px; padding: 11px; border: 1px solid var(--fb-line); border-radius: 9px; background: #f7f7f7; }
  .form-builder-sync > div { display: grid; gap: 2px; }
  .form-builder-sync strong { font-size: 12px; }
  .form-builder-sync span { color: var(--fb-muted); font-size: 11px; }
  .form-builder-sync .cms-btn { justify-self: start; }
  .form-builder-protected { padding: 10px 11px; border-radius: 9px; background: #f3f3f3; }
  .form-builder-inspector-actions { display: grid; gap: 8px; padding-top: 2px; border-top: 1px solid var(--fb-line); }
  .form-builder-confirm { display: grid; grid-template-columns: 1fr auto; gap: 8px; padding: 11px; border: 1px solid #efd0cd; border-radius: 10px; background: #fff7f6; }
  .form-builder-confirm p { grid-column: 1 / -1; margin: 0; color: #7f3333; font-size: 12.5px; line-height: 1.4; }
  .form-builder-text-button { flex: 0 0 auto; padding: 5px 7px; border: 0; border-radius: 7px; background: transparent; color: var(--fb-muted); font: inherit; font-size: 12px; font-weight: 800; cursor: pointer; }
  .form-builder-text-button:hover, .form-builder-text-button.is-active { background: #ededed; color: var(--fb-ink); }
  .form-builder-preview > header { background: #2c2620; color: #fff; border-bottom: 0; }
  .form-builder-preview > header .cms-eyebrow { color: #b8ad9c; }
  .form-builder-preview > header .form-builder-text-button { color: #d8cdbd; }
  .form-builder-preview > header .form-builder-text-button:hover { background: rgba(255,255,255,.1); color: #fff; }
  .form-builder-preview-window { padding: 20px; background: #fdfaf4; }
  .form-builder-preview-progress { position: relative; display: flex; justify-content: space-between; margin-bottom: 18px; padding-bottom: 9px; border-bottom: 2px solid #e7dfcc; color: #8a7d68; font-size: 9px; font-weight: 900; letter-spacing: .1em; }
  .form-builder-preview-progress i { position: absolute; left: 0; bottom: -2px; height: 2px; background: var(--fb-accent); transition: width .2s ease; }
  .form-builder-preview-window > h3 { margin: 0 0 18px; font-family: 'Bricolage Grotesque', serif; font-size: 22px; }
  .form-builder-preview-fields { display: grid; gap: 15px; }
  .form-builder-preview-fields > p { color: #8a7d68; font-size: 13px; }
  .form-builder-preview-field { display: grid; gap: 6px; padding: 3px; border: 1px solid transparent; border-radius: 9px; }
  .form-builder-preview-field.is-selected { border-color: rgba(222,148,69,.7); background: rgba(222,148,69,.07); }
  .form-builder-preview-field > label { color: #3a332a; font-size: 11px; font-weight: 800; }
  .form-builder-preview-field > label span { color: #c15b42; }
  .form-builder-preview-field input, .form-builder-preview-field textarea, .form-builder-preview-field select { width: 100%; box-sizing: border-box; padding: 9px 10px; border: 1px solid #ddd3c0; border-radius: 8px; background: #fff; color: #3a332a; font: inherit; font-size: 12.5px; }
  .form-builder-preview-options { display: grid; gap: 6px; }
  .form-builder-preview-options button { display: flex; align-items: center; gap: 8px; padding: 8px 9px; border: 1px solid #ddd3c0; border-radius: 8px; background: #fff; color: #3a332a; text-align: left; font-size: 12px; cursor: pointer; }
  .form-builder-preview-options button i { width: 10px; height: 10px; border: 1px solid #b8ad9c; border-radius: 50%; }
  .form-builder-preview-options button.is-selected { border-color: var(--fb-accent); background: #fff9ef; }
  .form-builder-preview-options button.is-selected i { border: 3px solid var(--fb-accent); }
  .form-builder-preview-options small { color: #8a7d68; }
  .form-builder-preview-window footer { display: flex; justify-content: space-between; gap: 8px; margin-top: 20px; }
  .form-builder-preview-window footer button { padding: 8px 12px; border: 1px solid #d8cdbd; border-radius: 999px; background: #fff; color: #3a332a; font-size: 11px; font-weight: 800; cursor: pointer; }
  .form-builder-preview-window footer button:last-child { margin-left: auto; background: #3a332a; color: #fff; border-color: #3a332a; }
  .form-builder-preview-window footer button:disabled { opacity: .35; cursor: not-allowed; }
  .form-builder-empty { justify-items: start; max-width: 520px; }
  @media (max-width: 1180px) { .form-builder-workspace { grid-template-columns: 220px minmax(300px, 1fr); } .form-builder-side { grid-column: 1 / -1; grid-template-columns: 1fr 1fr; } }
  @media (max-width: 760px) { .form-builder-workspace, .form-builder-side { grid-template-columns: 1fr; } .form-builder-side { grid-column: auto; } .form-builder-summary span:last-child { width: 100%; margin-left: 0; } }
`;
